import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { invoiceService } from '@/services/invoice.service';
import { clientService, Client } from '@/services/client.service';
import { productService } from '@/services/product.service';
import { Invoice, InvoiceStatus, PaymentMethod, InvoiceItem } from '@/types/invoice.types';
import { Product } from '@/types/product.types';
import { formatPrice } from '@/utils/formatters';
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import '@/styles/dashboard.css';

export const DetailFacture = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // États pour les listes de recherche
  const [clients, setClients] = useState<Client[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [createClientModalOpen, setCreateClientModalOpen] = useState(false);
  const [createClientFormData, setCreateClientFormData] = useState<{
    name: string;
    email: string;
    phone: string;
    address: string;
    city: string;
    postalCode: string;
    status: 'active' | 'inactive' | 'vip';
  }>({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    status: 'active',
  });
  const [creatingClient, setCreatingClient] = useState(false);
  const clientDropdownRef = useRef<HTMLDivElement>(null);
  const productDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (id && id !== 'nouvelle') {
      loadInvoice();
    } else {
      setLoading(false);
      setEditing(true);
    }
    loadClients();
    loadProducts();
  }, [id]);

  // Activer le mode édition si le paramètre edit=true est présent dans l'URL
  useEffect(() => {
    if (invoice && searchParams.get('edit') === 'true') {
      setEditing(true);
      // Nettoyer l'URL en retirant le paramètre edit
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('edit');
      const newSearch = newSearchParams.toString();
      navigate(`/admin/factures/${id}${newSearch ? `?${newSearch}` : ''}`, { replace: true });
    }
  }, [invoice, searchParams, id, navigate]);

  // Fermer les dropdowns quand on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (clientDropdownRef.current && !clientDropdownRef.current.contains(event.target as Node)) {
        setShowClientDropdown(false);
      }
      if (productDropdownRef.current && !productDropdownRef.current.contains(event.target as Node)) {
        setShowProductDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadClients = async () => {
    try {
      const data = await clientService.getAll();
      setClients(data);
    } catch (error) {
      console.error('Erreur lors du chargement des clients:', error);
    }
  };

  const loadProducts = async () => {
    try {
      const data = await productService.getAll();
      setProducts(data);
    } catch (error) {
      console.error('Erreur lors du chargement des produits:', error);
    }
  };

  const loadInvoice = async () => {
    try {
      setLoading(true);
      const data = await invoiceService.getById(parseInt(id!));
      setInvoice(data);
    } catch (error) {
      console.error('Erreur lors du chargement de la facture:', error);
      alert(t('adminInvoiceDetail.invoiceNotFound'));
      navigate('/admin/factures');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!invoice) return;
    
    try {
      setSaving(true);
      // Forcer le mode de paiement à 'card' (seulement carte disponible)
      const invoiceToSave = {
        ...invoice,
        payment_method: 'card' as PaymentMethod,
      };
      
      if (id === 'nouvelle') {
        const newInvoice = await invoiceService.create(invoiceToSave);
        navigate(`/admin/factures/${newInvoice.id}`);
      } else {
        await invoiceService.update(invoice.id, invoiceToSave);
        await loadInvoice();
      }
      setEditing(false);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      alert(t('adminInvoiceDetail.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus: InvoiceStatus) => {
    if (!invoice) return;
    
    // Ne rien faire si le statut est le même
    if (invoice.status === newStatus) {
      return;
    }
    
    try {
      const updatedInvoice = await invoiceService.updateStatus(invoice.id, newStatus);
      // Mettre à jour l'état local immédiatement pour un feedback instantané
      if (updatedInvoice) {
        setInvoice(updatedInvoice);
      }
      // Recharger depuis le serveur pour s'assurer d'avoir les données à jour
      await loadInvoice();
    } catch (error: any) {
      console.error('Erreur lors du changement de statut:', error);
      const errorMessage = error?.response?.data?.error || error?.message || t('adminInvoiceDetail.statusChangeError');
      alert(`Erreur: ${errorMessage}`);
      // Recharger la facture pour restaurer l'état précédent
      await loadInvoice();
    }
  };

  const handleDownloadPDF = async () => {
    if (!invoice) return;
    
    try {
      const blob = await invoiceService.generatePDF(invoice.id);
      
      // Vérifier que c'est bien un PDF
      if (!blob || blob.type !== 'application/pdf') {
        throw new Error(t('adminInvoiceDetail.invalidPdf'));
      }
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `facture-${invoice.invoice_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Erreur lors de la génération du PDF:', error);
      alert(error?.message || t('adminInvoiceDetail.pdfError'));
    }
  };

  // Gestion de la sélection de client
  const handleClientSelect = (client: Client) => {
    setSelectedClientId(client.id);
    setInvoice({
      ...currentInvoice,
      customer_name: client.name,
      customer_email: client.email,
      customer_phone: client.phone || null,
      customer_address: client.address || null,
      customer_city: client.city || null,
      customer_postal_code: client.postalCode || null,
    });
    setShowClientDropdown(false);
    setClientSearchTerm('');
  };

  // Gestion de la création de client
  const handleCreateClient = async () => {
    setCreatingClient(true);
    try {
      const newClient = await clientService.create({
        name: createClientFormData.name,
        email: createClientFormData.email,
        phone: createClientFormData.phone,
        address: createClientFormData.address,
        city: createClientFormData.city,
        postalCode: createClientFormData.postalCode,
        status: createClientFormData.status,
      });

      // Ajouter le nouveau client à la liste
      setClients(prevClients => [newClient, ...prevClients]);

      // Sélectionner automatiquement le nouveau client
      handleClientSelect(newClient);

      // Fermer le modal et réinitialiser le formulaire
      setCreateClientModalOpen(false);
      setCreateClientFormData({
        name: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        postalCode: '',
        status: 'active',
      });
    } catch (error: any) {
      console.error('Erreur lors de la création du client:', error);
      alert(error.message || t('adminInvoiceDetail.clientCreateError'));
    } finally {
      setCreatingClient(false);
    }
  };

  // Gestion de l'ajout de produit
  const handleAddProduct = (product: Product) => {
    const taxRate = 0.2; // 20% par défaut
    const unitPrice = product.price;
    const taxAmount = unitPrice * taxRate;
    const total = unitPrice + taxAmount;
    
    const newItem: InvoiceItem = {
      product_id: product.id,
      product_name: product.name,
      quantity: 1,
      unit_price: unitPrice,
      tax_rate: taxRate,
      tax_amount: taxAmount,
      total: total,
    };
    const updatedItems = [...currentInvoice.items, newItem];
    const subtotal = updatedItems.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0);
    const totalTax = updatedItems.reduce((sum, item) => sum + ((item.tax_amount || 0) * item.quantity), 0);
    const totalAmount = subtotal + totalTax - currentInvoice.discount_amount;
    
    setInvoice({
      ...currentInvoice,
      items: updatedItems,
      subtotal,
      total_tax: totalTax,
      total_amount: Math.max(0, totalAmount),
    });
    setShowProductDropdown(false);
    setProductSearchTerm('');
  };

  // Gestion de la suppression d'article
  const handleRemoveItem = (index: number) => {
    const updatedItems = currentInvoice.items.filter((_, i) => i !== index);
    const subtotal = updatedItems.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0);
    const totalTax = updatedItems.reduce((sum, item) => sum + (item.tax_amount || 0) * item.quantity, 0);
    const totalAmount = subtotal + totalTax - currentInvoice.discount_amount;
    
    setInvoice({
      ...currentInvoice,
      items: updatedItems,
      subtotal,
      total_tax: totalTax,
      total_amount: totalAmount,
    });
  };

  // Gestion de la modification de quantité
  const handleQuantityChange = (index: number, quantity: number) => {
    const updatedItems = [...currentInvoice.items];
    updatedItems[index].quantity = Math.max(1, quantity);
    const unitPrice = updatedItems[index].unit_price;
    const taxRate = updatedItems[index].tax_rate || 0;
    const taxAmount = unitPrice * taxRate;
    updatedItems[index].tax_amount = taxAmount;
    updatedItems[index].total = (unitPrice + taxAmount) * quantity;
    
    const subtotal = updatedItems.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0);
    const totalTax = updatedItems.reduce((sum, item) => sum + ((item.tax_amount || 0) * item.quantity), 0);
    const totalAmount = subtotal + totalTax - currentInvoice.discount_amount;
    
    setInvoice({
      ...currentInvoice,
      items: updatedItems,
      subtotal,
      total_tax: totalTax,
      total_amount: Math.max(0, totalAmount),
    });
  };

  // Filtrer les clients
  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(clientSearchTerm.toLowerCase()) ||
    client.email.toLowerCase().includes(clientSearchTerm.toLowerCase())
  );

  // Filtrer les produits
  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(productSearchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div style={{ padding: '60px 20px', textAlign: 'center' }}>
                <div style={{ color: '#999' }}>{t('adminInvoiceDetail.loadingInvoice')}</div>
              </div>
    );
  }

  if (!invoice && id !== 'nouvelle') {
    return null;
  }

  const currentInvoice = invoice || {
    id: 0,
    invoice_number: '',
    order_id: null,
    invoice_date: new Date().toISOString(),
    customer_name: '',
    customer_email: '',
    customer_phone: null,
    customer_address: null,
    customer_city: null,
    customer_postal_code: null,
    customer_vat: null,
    items: [],
    taxes: [],
    subtotal: 0,
    discount_amount: 0,
    total_tax: 0,
    total_amount: 0,
    payment_method: 'card' as PaymentMethod,
    status: 'draft' as InvoiceStatus,
    due_date: null,
    notes: null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };

  const invoicePdfUrl =
    typeof window !== 'undefined' && currentInvoice.id
      ? `${window.location.origin}/api/invoices/${currentInvoice.id}/pdf`
      : '';
  const qrCodeUrl = invoicePdfUrl
    ? `https://api.qrserver.com/v1/create-qr-code/?size=140x140&data=${encodeURIComponent(invoicePdfUrl)}`
    : '';

  return (
    <>

    {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <div>
                <button
                  className="action-button"
                  onClick={() => navigate('/admin/factures')}
                  style={{ marginBottom: '12px' }}
                >
                  ← Retour
                </button>
                <h1 style={{ fontSize: '28px', fontWeight: 700, margin: 0 }}>
                  {id === 'nouvelle' ? t('adminInvoiceDetail.newInvoice') : t('adminInvoiceDetail.invoice', { number: currentInvoice.invoice_number })}
                </h1>
                {currentInvoice.order_id && (
                  <div style={{ marginTop: '8px', fontSize: '14px', color: '#666' }}>
                    <span>{t('adminInvoiceDetail.associatedOrder')} </span>
                    <button
                      onClick={() => navigate(`/admin/commandes?order=${currentInvoice.order_id}`)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#6a2340',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        fontWeight: 600
                      }}
                    >
                      Voir la commande #{currentInvoice.order_id}
                    </button>
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                {!editing && (
                  <>
                    <button className="action-button" onClick={() => setEditing(true)}>
                      Modifier
                    </button>
                    <button className="action-button" onClick={handleDownloadPDF}>
                      Télécharger PDF
                    </button>
                    <select
                      className="filter-select"
                      value={currentInvoice.status}
                      onChange={(e) => handleStatusChange(e.target.value as InvoiceStatus)}
                      style={{ padding: '10px 16px' }}
                    >
                      <option value="draft">{t('adminInvoiceDetail.draft')}</option>
                      <option value="sent">{t('adminInvoiceDetail.sent')}</option>
                      <option value="paid">{t('adminInvoiceDetail.paid')}</option>
                      <option value="overdue">{t('adminInvoiceDetail.overdue')}</option>
                      <option value="cancelled">{t('adminInvoiceDetail.cancelled')}</option>
                    </select>
                  </>
                )}
                {editing && (
                  <>
                    <button className="action-button" onClick={() => {
                      setEditing(false);
                      if (invoice) loadInvoice();
                    }}>
                      Annuler
                    </button>
                    <button className="action-button primary" onClick={handleSave} disabled={saving}>
                      {saving ? t('adminInvoiceDetail.saving') : t('adminInvoiceDetail.save')}
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Formulaire */}
            <div className="table-card">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                {/* Informations client */}
                <div>
                  <h3 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '16px' }}>{t('adminInvoiceDetail.client')}</h3>
                  <div className="form-group">
                    <label>{t('adminInvoiceDetail.fullName')}</label>
                    {editing ? (
                      <div style={{ position: 'relative' }} ref={clientDropdownRef}>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <div style={{ flex: 1, position: 'relative' }}>
                            <input
                              type="text"
                              value={clientSearchTerm || currentInvoice.customer_name}
                              onChange={(e) => {
                                setClientSearchTerm(e.target.value);
                                setShowClientDropdown(true);
                              }}
                              onFocus={() => setShowClientDropdown(true)}
                              placeholder={t('adminInvoiceDetail.searchClient')}
                              style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                            />
                            {showClientDropdown && filteredClients.length > 0 && (
                              <div style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                right: 0,
                                backgroundColor: 'white',
                                border: '1px solid #ddd',
                                borderRadius: '6px',
                                marginTop: '4px',
                                maxHeight: '200px',
                                overflowY: 'auto',
                                zIndex: 1000,
                                boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                              }}>
                                {filteredClients.map(client => (
                                  <div
                                    key={client.id}
                                    onClick={() => handleClientSelect(client)}
                                    style={{
                                      padding: '12px',
                                      cursor: 'pointer',
                                      borderBottom: '1px solid #eee',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: '4px'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                                  >
                                    <strong>{client.name}</strong>
                                    <span style={{ fontSize: '12px', color: '#666' }}>{client.email}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => setCreateClientModalOpen(true)}
                            style={{
                              padding: '10px 12px',
                              border: '1px solid #ddd',
                              borderRadius: '6px',
                              backgroundColor: '#8C1843',
                              color: 'white',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            title={t('adminInvoiceDetail.addNewClient')}
                          >
                            <PlusIcon style={{ width: '20px', height: '20px' }} />
                          </button>
                        </div>
                      </div>
                    ) : (
                    <input
                      type="text"
                      value={currentInvoice.customer_name}
                        disabled
                        style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', backgroundColor: '#f5f5f5' }}
                    />
                    )}
                  </div>
                  <div className="form-group">
                    <label>{t('adminInvoiceDetail.email')}</label>
                    <input
                      type="email"
                      value={currentInvoice.customer_email}
                      onChange={(e) => setInvoice({ ...currentInvoice, customer_email: e.target.value })}
                      disabled={!editing}
                      style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('adminInvoiceDetail.phone')}</label>
                    <input
                      type="text"
                      value={currentInvoice.customer_phone || ''}
                      onChange={(e) => setInvoice({ ...currentInvoice, customer_phone: e.target.value })}
                      disabled={!editing}
                      style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('adminInvoiceDetail.address')}</label>
                    <input
                      type="text"
                      value={currentInvoice.customer_address || ''}
                      onChange={(e) => setInvoice({ ...currentInvoice, customer_address: e.target.value })}
                      disabled={!editing}
                      style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                    />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px' }}>
                    <div className="form-group">
                      <label>{t('adminInvoiceDetail.city')}</label>
                      <input
                        type="text"
                        value={currentInvoice.customer_city || ''}
                        onChange={(e) => setInvoice({ ...currentInvoice, customer_city: e.target.value })}
                        disabled={!editing}
                        style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                      />
                    </div>
                    <div className="form-group">
                      <label>{t('adminInvoiceDetail.postalCode')}</label>
                      <input
                        type="text"
                        value={currentInvoice.customer_postal_code || ''}
                        onChange={(e) => setInvoice({ ...currentInvoice, customer_postal_code: e.target.value })}
                        disabled={!editing}
                        style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>{t('adminInvoiceDetail.vatNumber')}</label>
                    <input
                      type="text"
                      value={currentInvoice.customer_vat || ''}
                      onChange={(e) => setInvoice({ ...currentInvoice, customer_vat: e.target.value })}
                      disabled={!editing}
                      style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                    />
                  </div>
                </div>

                {/* Informations facture */}
                <div>
                  <h3 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '16px' }}>{t('adminInvoiceDetail.invoiceSection')}</h3>
                  <div className="form-group">
                    <label>{t('adminInvoiceDetail.invoiceNumber')}</label>
                    <input
                      type="text"
                      value={currentInvoice.invoice_number || 'Généré automatiquement'}
                      disabled
                      style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', backgroundColor: '#f5f5f5' }}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('adminInvoiceDetail.date')}</label>
                    <input
                      type="date"
                      value={currentInvoice.invoice_date ? currentInvoice.invoice_date.split('T')[0] : ''}
                      onChange={(e) => setInvoice({ ...currentInvoice, invoice_date: e.target.value })}
                      disabled={!editing}
                      style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('adminInvoiceDetail.dueDate')}</label>
                    <input
                      type="date"
                      value={currentInvoice.due_date ? currentInvoice.due_date.split('T')[0] : ''}
                      onChange={(e) => setInvoice({ ...currentInvoice, due_date: e.target.value || null })}
                      disabled={!editing}
                      style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('adminInvoiceDetail.paymentMethod')}</label>
                    <select
                      value="card"
                      disabled={true}
                      style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', backgroundColor: '#f5f5f5' }}
                    >
                      <option value="card">{t('adminInvoiceDetail.card')}</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>{t('adminInvoiceDetail.status')}</label>
                    <select
                      value={currentInvoice.status}
                      onChange={(e) => setInvoice({ ...currentInvoice, status: e.target.value as InvoiceStatus })}
                      disabled={!editing}
                      style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                    >
                      <option value="draft">{t('adminInvoiceDetail.draft')}</option>
                      <option value="sent">{t('adminInvoiceDetail.sent')}</option>
                      <option value="paid">{t('adminInvoiceDetail.paid')}</option>
                      <option value="overdue">{t('adminInvoiceDetail.overdue')}</option>
                      <option value="cancelled">{t('adminInvoiceDetail.cancelled')}</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>{t('adminInvoiceDetail.notes')}</label>
                    <textarea
                      value={currentInvoice.notes || ''}
                      onChange={(e) => setInvoice({ ...currentInvoice, notes: e.target.value })}
                      disabled={!editing}
                      rows={4}
                      style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontFamily: 'inherit' }}
                    />
                  </div>
                </div>
              </div>

              {/* Lignes de facture */}
              <div style={{ marginTop: '32px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: 700, margin: 0 }}>{t('adminInvoiceDetail.items')}</h3>
                  {editing && (
                    <div style={{ position: 'relative', width: '300px' }} ref={productDropdownRef}>
                      <input
                        type="text"
                        value={productSearchTerm}
                        onChange={(e) => {
                          setProductSearchTerm(e.target.value);
                          setShowProductDropdown(true);
                        }}
                        onFocus={() => setShowProductDropdown(true)}
                        placeholder={t('adminInvoiceDetail.searchProduct')}
                        style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                      />
                      {showProductDropdown && filteredProducts.length > 0 && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          backgroundColor: 'white',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          marginTop: '4px',
                          maxHeight: '200px',
                          overflowY: 'auto',
                          zIndex: 1000,
                          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                        }}>
                          {filteredProducts.map(product => (
                            <div
                              key={product.id}
                              onClick={() => handleAddProduct(product)}
                              style={{
                                padding: '12px',
                                cursor: 'pointer',
                                borderBottom: '1px solid #eee',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                            >
                              <div>
                                <strong>{product.name}</strong>
                                <div style={{ fontSize: '12px', color: '#666' }}>{formatPrice(product.price)}</div>
                              </div>
                              <PlusIcon style={{ width: '20px', height: '20px', color: '#8C1843' }} />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <table className="stock-table">
                  <thead>
                    <tr>
                      <th>{t('adminInvoiceDetail.product')}</th>
                      <th>{t('adminInvoiceDetail.quantity')}</th>
                      <th>{t('adminInvoiceDetail.unitPrice')}</th>
                      <th>{t('adminInvoiceDetail.tva')}</th>
                      <th>{t('adminInvoiceDetail.total')}</th>
                      {editing && <th>{t('adminInvoiceDetail.actions')}</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {currentInvoice.items.length === 0 ? (
                      <tr>
                        <td colSpan={editing ? 6 : 5} style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                          {t('adminInvoiceDetail.noItems')}
                        </td>
                      </tr>
                    ) : (
                      currentInvoice.items.map((item, index) => (
                        <tr key={index}>
                          <td>{item.product_name}</td>
                          <td>
                            {editing ? (
                              <input
                                type="number"
                                min="1"
                                value={item.quantity}
                                onChange={(e) => handleQuantityChange(index, parseInt(e.target.value) || 1)}
                                style={{ width: '60px', padding: '6px', border: '1px solid #ddd', borderRadius: '4px' }}
                              />
                            ) : (
                              item.quantity
                            )}
                          </td>
                          <td>{formatPrice(item.unit_price)}</td>
                          <td>{item.tax_rate ? `${(item.tax_rate * 100).toFixed(0)}%` : '-'}</td>
                          <td><strong>{formatPrice(item.total)}</strong></td>
                          {editing && (
                            <td>
                              <button
                                onClick={() => handleRemoveItem(index)}
                                style={{
                                  padding: '6px 10px',
                                  border: 'none',
                                  backgroundColor: '#ef4444',
                                  color: 'white',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}
                                title={t('adminInvoiceDetail.delete')}
                              >
                                <XMarkIcon style={{ width: '16px', height: '16px' }} />
                              </button>
                            </td>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Totaux */}
              <div className="invoice-summary-row">
                {qrCodeUrl && (
                  <div className="invoice-qr">
                    <div className="invoice-qr-label">{t('adminInvoiceDetail.digitalVersion')}</div>
                    <a href={invoicePdfUrl} target="_blank" rel="noreferrer">
                      <img src={qrCodeUrl} alt="QR code de la facture" />
                    </a>
                    <div className="invoice-qr-caption">{t('adminInvoiceDetail.scanToOpen')}</div>
                  </div>
                )}
                <div className="invoice-totals">
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                    <span>{t('adminInvoiceDetail.subtotalExcl')}</span>
                    <strong>{formatPrice(currentInvoice.subtotal)}</strong>
                  </div>
                  {currentInvoice.discount_amount > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                      <span>{t('adminInvoiceDetail.discount')}</span>
                      <strong>-{formatPrice(currentInvoice.discount_amount)}</strong>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                    <span>{t('adminInvoiceDetail.tva')}</span>
                    <strong>{formatPrice(currentInvoice.total_tax)}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '16px 0', fontSize: '20px', fontWeight: 700, borderTop: '2px solid #333' }}>
                    <span>{t('adminInvoiceDetail.totalIncl')}</span>
                    <strong>{formatPrice(currentInvoice.total_amount)}</strong>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal de création de client */}
            {createClientModalOpen && (
              <div 
                className="modal-overlay" 
                onClick={() => !creatingClient && setCreateClientModalOpen(false)}
                role="dialog"
                aria-modal="true"
                aria-labelledby="create-client-modal-title"
              >
                <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
                  <div className="modal-header">
                    <h3 id="create-client-modal-title" className="modal-title">
                      {t('adminInvoiceDetail.createClient')}
                    </h3>
                    <button 
                      className="modal-close"
                      onClick={() => !creatingClient && setCreateClientModalOpen(false)}
                      aria-label={t('adminInvoiceDetail.close')}
                      disabled={creatingClient}
                    >
                      <XMarkIcon className="btn-icon" />
                    </button>
                  </div>
                  <div className="modal-body">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      <div className="form-group">
                        <label className="form-label">{t('adminInvoiceDetail.fullName')}</label>
                        <input
                          type="text"
                          className="form-input"
                          value={createClientFormData.name}
                          onChange={(e) => setCreateClientFormData({ ...createClientFormData, name: e.target.value })}
                          disabled={creatingClient}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">{t('adminInvoiceDetail.email')}</label>
                        <input
                          type="email"
                          className="form-input"
                          value={createClientFormData.email}
                          onChange={(e) => setCreateClientFormData({ ...createClientFormData, email: e.target.value })}
                          disabled={creatingClient}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">{t('adminInvoiceDetail.phone')} *</label>
                        <input
                          type="tel"
                          className="form-input"
                          value={createClientFormData.phone}
                          onChange={(e) => setCreateClientFormData({ ...createClientFormData, phone: e.target.value })}
                          disabled={creatingClient}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">{t('adminInvoiceDetail.address')}</label>
                        <input
                          type="text"
                          className="form-input"
                          value={createClientFormData.address}
                          onChange={(e) => setCreateClientFormData({ ...createClientFormData, address: e.target.value })}
                          disabled={creatingClient}
                        />
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                        <div className="form-group">
                          <label className="form-label">{t('adminInvoiceDetail.city')}</label>
                          <input
                            type="text"
                            className="form-input"
                            value={createClientFormData.city}
                            onChange={(e) => setCreateClientFormData({ ...createClientFormData, city: e.target.value })}
                            disabled={creatingClient}
                          />
                        </div>
                        <div className="form-group">
                          <label className="form-label">{t('adminInvoiceDetail.postalCode')}</label>
                          <input
                            type="text"
                            className="form-input"
                            value={createClientFormData.postalCode}
                            onChange={(e) => setCreateClientFormData({ ...createClientFormData, postalCode: e.target.value })}
                            disabled={creatingClient}
                          />
                        </div>
                      </div>
                      <div className="form-group">
                        <label className="form-label">{t('adminInvoiceDetail.status')} *</label>
                        <select
                          className="form-select"
                          value={createClientFormData.status}
                          onChange={(e) => setCreateClientFormData({ ...createClientFormData, status: e.target.value as 'active' | 'inactive' | 'vip' })}
                          disabled={creatingClient}
                          required
                        >
                          <option value="active">{t('adminClients.active')}</option>
                          <option value="inactive">{t('adminClients.inactive')}</option>
                          <option value="vip">{t('adminClients.vip')}</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', padding: '16px' }}>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => setCreateClientModalOpen(false)}
                      disabled={creatingClient}
                    >
                      Annuler
                    </button>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={handleCreateClient}
                      disabled={creatingClient || !createClientFormData.name || !createClientFormData.email || !createClientFormData.phone}
                    >
                      {creatingClient ? 'Création...' : 'Créer'}
                    </button>
                  </div>
                </div>
              </div>
            )}
  
    </>
  );
};
